<template>
  <div>
    <PageHeader :subtitle="subtitle" />

    <ProductSwitch
      :value="activeTab.label"
      :counts="TABS"
      :show-count="false"
      @change="selectTab"
    />

    <template v-if="tab === 'logs'">
      <FilterBar
        :filters="filterSchema"
        :value="filters"
        :search.sync="search"
        :date-range.sync="dateRange"
        :result-count="error ? null : totalCount"
        result-label="messages"
        search-placeholder="Search user ID, mobile or name"
        @change="reload"
        @reset="resetFilters"
      >
        <template #actions>
          <!-- Static = everything the growth agent did not choose: the event
               messages (welcome, disbursal, rejection), panel broadcasts and the
               retired journey's rows. Hidden by default so the table shows what
               the agent decided. Shared with the User history tab. -->
          <v-checkbox
            v-model="showStatic"
            label="See static"
            title="Messages the growth agent did not choose: welcome, disbursal and rejection messages, and panel broadcasts"
            dense
            hide-details
            class="kf-static mt-0 pt-0 mr-2"
          />
          <v-btn
            small
            text
            class="text-none"
            :disabled="!!loading"
            @click="load"
          >
            <LucideIcon name="refresh-cw" :size="15" class="mr-1" :spin="!!loading" />
            Refresh
          </v-btn>
        </template>
      </FilterBar>

      <DataTable
        class="mt-3"
        title="Notification history"
        :headers="headers"
        :items="items"
        :loading="loading"
        :error="error"
        :page="page"
        :total-pages="totalPages"
        :total-count="totalCount"
        :page-length="pageLength"
        item-key="log_id"
        empty-title="No notifications logged"
        empty-message="Nothing matches these filters. Try widening the date range or clearing the search."
        empty-icon="inbox"
        @page-change="onPage"
        @retry="load"
      >
        <template #[`item.created`]="{ item }">
          <span class="kf-nowrap">{{ fmt.dateTimeIst(item.created) }}</span>
        </template>

        <template #[`item.user_id`]="{ item }">
          <div class="kf-user">
            <span v-if="item.customer_name">{{ item.customer_name }}</span>
            <span v-else class="kf-muted">No name</span>
            <div class="kf-detail">ID {{ item.user_id }}</div>
          </div>
        </template>

        <template #[`item.mobile`]="{ item }">
          <span class="kf-nowrap">{{ item.mobile || "-" }}</span>
        </template>

        <template #[`item.channel`]="{ item }">
          <span class="kf-channel">
            <LucideIcon :name="channelIcon(item.channel)" :size="14" />
            {{ channelLabel(item.channel) }}
          </span>
        </template>

        <template #[`item.message`]="{ item }">
          <div class="kf-message" :title="item.body">
            <div class="kf-message__title">{{ item.title }}</div>
            <div v-if="item.body" class="kf-message__body">{{ item.body }}</div>
          </div>
        </template>

        <template #[`item.borrower_type_label`]="{ item }">
          <span v-if="item.borrower_type_label" class="kf-nowrap">
            {{ item.borrower_type_label }}
          </span>
          <span
            v-else
            class="kf-muted kf-nowrap"
            title="Logged before the borrower's status was recorded"
          >
            Not recorded
          </span>
        </template>

        <template #[`item.outcome_label`]="{ item }">
          <div>
            <StatusChip
              :status="item.outcome_label"
              :color="outcomeColor(item.outcome_group)"
              small
            />
            <div
              v-if="item.outcome_group === 'skipped' && item.outcome_detail"
              class="kf-detail"
            >
              {{ readable(item.outcome) }} · {{ item.outcome_detail }}
            </div>
            <div v-else-if="item.outcome_group === 'skipped'" class="kf-detail">
              {{ readable(item.outcome) }}
            </div>
          </div>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn icon small title="View message" @click="open(item)">
            <LucideIcon name="eye" :size="17" />
          </v-btn>
        </template>
      </DataTable>

      <NotificationLogDialog v-model="dialog" :log="selected" />
    </template>

    <!-- Mounted on first visit and kept, so switching tabs keeps its page and
         filters. -->
    <UserHistory
      v-if="usersVisited"
      v-show="tab === 'users'"
      :show-static.sync="showStatic"
    />
  </div>
</template>

<script>
import DataTable from "@/components/karrofin/DataTable.vue";
import FilterBar from "@/components/karrofin/FilterBar.vue";
import PageHeader from "@/components/karrofin/PageHeader.vue";
import StatusChip from "@/components/karrofin/StatusChip.vue";
import NotificationLogDialog from "@/components/karrofin/NotificationLogDialog.vue";
import ProductSwitch from "@/components/karrofin/ProductSwitch.vue";
import UserHistory from "./UserHistory.vue";
import api from "@/app-js/karrofin/api";
import fmt from "@/app-js/karrofin/format";
import { CHANNELS } from "@/app-js/karrofin/constants";

/* The server's outcome filter values (notification/history.py OUTCOME_FILTERS).
   "Provider accepted" is deliberately not "Delivered": nothing reports past
   the provider taking the message. */
const OUTCOMES = [
  { value: "would_send", label: "Would send (dry run)", color: "#1976D2" },
  { value: "skipped", label: "Skipped (dry run)", color: "#78909C" },
  { value: "provider_accepted", label: "Provider accepted", color: "#2E7D32" },
  { value: "failed", label: "Failed", color: "#C62828" },
];

function emptyFilters() {
  return { channel: null, title: [], outcome: null };
}

/* The screen's two tabs. `key` is what ?tab= carries; User history is the
   default and leaves the query empty. */
const TABS = [
  {
    key: "users",
    label: "User history",
    subtitle:
      "Every borrower who received a message, most recent first. Click a borrower to see each message they got, with the borrower's status when each one was sent. Tick See static to include everything the agent did not send: welcome, disbursal and rejection messages, panel broadcasts, and loan, payment, EMI and KYC messages.",
  },
  {
    key: "logs",
    label: "Logs",
    subtitle:
      "Every message the growth agent sent or proposed under a dry run, newest first, with the borrower's status when it was triggered. Tick See static to include the welcome, disbursal and rejection messages and panel broadcasts.",
  },
];

function tabFromQuery(value) {
  return TABS.some((t) => t.key === value) ? value : "users";
}

export default {
  name: "KarroFinNotificationHistory",
  components: {
    DataTable,
    FilterBar,
    PageHeader,
    StatusChip,
    NotificationLogDialog,
    ProductSwitch,
    UserHistory,
  },
  data() {
    const tab = tabFromQuery(this.$route.query.tab);
    return {
      fmt,
      TABS,
      tab,
      usersVisited: tab === "users",
      // The Logs tab loads on its first visit, and again after "See static"
      // changed while it was hidden.
      logsLoaded: false,
      logsStale: false,
      loading: false,
      error: null,
      items: [],
      page: 1,
      pageLength: 20,
      totalPages: 1,
      totalCount: null,
      search: "",
      dateRange: [],
      filters: emptyFilters(),
      showStatic: false,
      dialog: false,
      selected: null,
      // Each load takes a ticket; a reply that is not the latest is dropped, so
      // a slow earlier request cannot overwrite the rows of a newer filter.
      ticket: 0,
      titleTicket: 0,
      filterSchema: [
        {
          key: "channel",
          label: "Channel",
          type: "select",
          options: CHANNELS,
          itemText: "label",
          itemValue: "value",
          placeholder: "Any channel",
          pinned: true,
        },
        {
          // Options come from the server (every distinct title in the log),
          // loaded in loadTitles().
          key: "title",
          label: "Title",
          type: "multiselect",
          options: [],
          placeholder: "Any title",
          pinned: true,
        },
        {
          key: "outcome",
          label: "Outcome",
          type: "select",
          options: OUTCOMES,
          itemText: "label",
          itemValue: "value",
          placeholder: "Any outcome",
          pinned: true,
        },
      ],
      headers: [
        { text: "User", value: "user_id" },
        { text: "Mobile", value: "mobile" },
        { text: "Trigger time (IST)", value: "created" },
        { text: "Message", value: "message" },
        { text: "Channel", value: "channel" },
        { text: "Status", value: "borrower_type_label" },
        { text: "Message outcome", value: "outcome_label" },
        { text: "", value: "actions", align: "end" },
      ],
    };
  },
  computed: {
    activeTab() {
      return TABS.find((t) => t.key === this.tab) || TABS[0];
    },
    subtitle() {
      return this.activeTab.subtitle;
    },
  },
  created() {
    if (this.tab === "logs") this.loadLogs();
  },
  watch: {
    // The title list follows the date range, so it only offers titles that
    // can match.
    dateRange() {
      this.loadTitles();
    },
    showStatic() {
      if (this.tab === "logs") {
        this.reload();
        this.loadTitles();
      } else {
        this.logsStale = true;
      }
    },
    // Back / forward between ?tab= values.
    "$route.query.tab"(value) {
      this.showTab(tabFromQuery(value));
    },
  },
  methods: {
    selectTab(label) {
      const next = TABS.find((t) => t.label === label);
      if (!next || next.key === this.tab) return;
      const query = { ...this.$route.query, tab: next.key === "users" ? undefined : next.key };
      // A history entry per tab, so Back returns to the previous tab (the
      // $route.query.tab watcher). Duplicate navigation rejects in vue-router
      // 3; nothing to do then.
      this.$router.push({ query }).catch(() => {});
      this.showTab(next.key);
    },
    showTab(key) {
      this.tab = key;
      if (key === "users") this.usersVisited = true;
      if (key === "logs" && (!this.logsLoaded || this.logsStale)) {
        this.page = 1;
        this.loadLogs();
      }
    },
    loadLogs() {
      this.logsLoaded = true;
      this.logsStale = false;
      this.load();
      this.loadTitles();
    },
    load() {
      const ticket = ++this.ticket;
      this.loading = true;
      this.error = null;
      api
        .getAutomationHistory({
          page_number: this.page,
          page_length: this.pageLength,
          date_range: this.dateRange.length === 2 ? this.dateRange : undefined,
          channel: this.filters.channel || undefined,
          title: this.filters.title && this.filters.title.length ? this.filters.title : undefined,
          outcome: this.filters.outcome || undefined,
          search: (this.search || "").trim() || undefined,
          static: this.showStatic ? 1 : 0,
        })
        .then((data) => {
          if (ticket !== this.ticket) return;
          this.items = data.results;
          this.totalPages = data.total_page_count;
          this.totalCount = data.total_count;
          this.page = data.page_number;
        })
        .catch((err) => {
          if (ticket !== this.ticket) return;
          this.error = err;
          this.items = [];
        })
        .finally(() => {
          if (ticket === this.ticket) this.loading = false;
        });
    },
    reload() {
      this.page = 1;
      this.load();
    },
    /**
     * The Title filter's options. Titles already picked stay in the list even
     * when the new date range has none of them, so a selection is never left
     * applied but invisible. A failure keeps the previous list: the table
     * itself still loads, and its own error state is the one that matters.
     */
    loadTitles() {
      const ticket = ++this.titleTicket;
      api
        .getAutomationHistoryTitles({
          date_range: this.dateRange.length === 2 ? this.dateRange : undefined,
          static: this.showStatic ? 1 : 0,
        })
        .then((titles) => {
          if (ticket !== this.titleTicket) return;
          const picked = (this.filters.title || []).filter(
            (t) => titles.indexOf(t) === -1
          );
          const field = this.filterSchema.find((f) => f.key === "title");
          field.options = picked.concat(titles);
        })
        .catch(() => {});
    },
    onPage(page) {
      this.page = page;
      this.load();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    resetFilters() {
      this.filters = emptyFilters();
      this.search = "";
      this.dateRange = [];
      this.showStatic = false;
      this.reload();
    },
    open(item) {
      this.selected = item;
      this.dialog = true;
    },
    channelLabel(value) {
      const c = CHANNELS.find((x) => x.value === value);
      return c ? c.label : value || "-";
    },
    channelIcon(value) {
      const c = CHANNELS.find((x) => x.value === value);
      return c ? c.icon : "send";
    },
    outcomeColor(group) {
      const o = OUTCOMES.find((x) => x.value === group);
      return o ? o.color : "";
    },
    readable(value) {
      return String(value || "")
        .replace(/^skipped_/, "")
        .replace(/_/g, " ");
    },
  },
};
</script>

<style scoped>
.kf-nowrap {
  white-space: nowrap;
}
.kf-muted {
  color: #54636f;
}
.kf-static {
  flex: 0 0 auto;
  white-space: nowrap;
}
.kf-channel {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}
.kf-user {
  min-width: 120px;
  line-height: 1.35;
}
.kf-message {
  max-width: 400px;
  min-width: 260px;
  line-height: 1.45;
}
/* The title is a caption; the message text is what the row is about. */
.kf-message__title {
  font-size: var(--kf-fs-meta);
  font-weight: 600;
  color: #54636f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.kf-message__body {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: var(--kf-fs-hint);
  color: #1f2d36;
}
.kf-detail {
  margin-top: 2px;
  font-size: var(--kf-fs-label);
  color: #54636f;
  white-space: nowrap;
}
</style>
